import { useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import SeoScript from "@/components/feature/SeoScript";
import AnimeNavbar from "@/components/feature/AnimeNavbar";
import AnimeFooter from "@/components/feature/AnimeFooter";
import usePageTitle from "@/hooks/usePageTitle";
import { characters, galleryImages, siteName } from "@/mocks/anime";

const heroBg =
  "https://readdy.ai/api/search-image?query=anime%20illustration%20of%20silhouetted%20character%20portraits%20lined%20against%20a%20warm%20orange%20sunset%20sky%20with%20large%20dark%20areas%20for%20text%20contrast%2C%20ink%20wash%20and%20sakuga%20style%2C%20golden%20backlight%2C%20cinematic%20manga%20aesthetic%2C%20clean%20composition&width=1600&height=600&seq=characters-hero&orientation=landscape";

function CharactersGrid() {
  const [active, setActive] = useState("全部");
  const roles = ["全部", ...Array.from(new Set(characters.map((c) => c.role)))];
  const filtered = active === "全部" ? characters : characters.filter((c) => c.role === active);

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 md:px-6">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 flex items-center gap-1 text-sm text-foreground-600">
          <i className="ri-account-box-line"></i>
          按身份筛选
        </span>
        {roles.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setActive(r)}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors cursor-pointer ${
              active === r
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-primary-50"
            }`}
          >
            {r}
          </button>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((c) => (
          <article
            key={c.name}
            className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all hover:-translate-y-0.5 hover:border-accent-200"
          >
            <div className="p-6 pb-4">
              <div className="flex items-center justify-between">
                <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">{c.badge}</span>
                <span className="text-xs text-foreground-500">{c.role}</span>
              </div>
              <h2 className="mt-4 text-lg font-medium text-foreground-900">{c.name}</h2>
              <p className="flex items-center gap-1 text-sm text-primary-600">
                <i className="ri-double-quotes-l text-xs"></i>
                {c.tagline}
              </p>
            </div>
            <div className="border-t border-background-200 px-6 py-4">
              <p className="text-sm leading-6 text-foreground-600">{c.desc}</p>
            </div>
          </article>
        ))}
      </div>

      <h3 className="font-heading mt-14 text-xl text-foreground-900 md:text-2xl">故事氛围画廊</h3>
      <p className="mt-2 text-sm text-foreground-600">几张氛围图，帮你更快进入《秘密教学动漫》的世界。</p>
      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {galleryImages.map((g) => (
          <figure key={g.title} className="group overflow-hidden rounded-lg border border-background-200">
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={g.img}
                alt={g.title}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <figcaption className="flex items-center justify-between px-4 py-3">
              <span className="text-sm font-medium text-foreground-900">{g.title}</span>
              <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-700">{g.tag}</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

export default function Characters() {
  usePageTitle("角色图鉴");
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://mysteryplot.github.io/" },
          { "@type": "ListItem", position: 2, name: "角色图鉴" },
        ],
      },
      {
        "@type": "CollectionPage",
        name: "秘密教学动漫角色图鉴",
        url: "https://mysteryplot.github.io/characters",
        description: "秘密教学动漫主要角色介绍与身份关系图鉴。",
      },
    ],
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SeoScript data={jsonLd} />
      <AnimeNavbar />
      <main className="flex-1">
        <PageHero
          tag={siteName}
          title="秘密教学动漫角色图鉴"
          desc="主角、引路人、同伴与对手，每一位角色都有自己的立场与故事。这份图鉴帮你理清他们之间的关系，看懂每一个选择的背后动机。"
          bg={heroBg}
        />
        <nav className="border-b border-background-200 bg-background-100" aria-label="面包屑">
          <div className="mx-auto flex max-w-7xl items-center gap-1 px-4 py-3 text-sm md:px-6">
            <Link to="/" className="text-foreground-500 hover:text-primary-600 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="text-foreground-800">角色图鉴</span>
          </div>
        </nav>
        <CharactersGrid />
      </main>
      <AnimeFooter />
    </div>
  );
}